import React from 'react';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import CodeIcon from '@mui/icons-material/Code';
import RateReviewIcon from '@mui/icons-material/RateReview';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ContactForm from '../ContactForm';
import CountUp from './CountUp';
import { technologies } from '../../data/learn';
import { courses } from '../../data/courses';

const lessonCount = technologies.reduce((sum, t) => sum + t.lessons.length, 0);

const mentorPoints = [
  { icon: ArchitectureIcon, title: 'Built systems at scale', text: 'Architects and staff engineers who have designed systems serving millions of users.' },
  { icon: CodeIcon, title: 'Production-grade code', text: 'Learn the patterns, workflows and standards used at product companies.' },
  { icon: RateReviewIcon, title: 'Real code reviews', text: 'Personal feedback on your projects, the way senior engineers review at work.' },
  { icon: WorkspacePremiumIcon, title: 'Career guidance', text: 'Interview preparation, portfolio and resume reviews from people who hire.' }
];

export const MentorSection = () => (
  <section className="home-section" id="contact">
    <div className="container">
      <div className="mentor-panel" data-reveal>
        <div className="mentor-copy">
          <span className="section-eyebrow">Your mentors</span>
          <h2>Learn from engineers who have done the job</h2>
          <p>
            Our instructors are software architects and staff engineers with 15+ years at leading product companies.
            They teach what actually matters on the job, and in the interview.
          </p>
          <div className="mentor-stats">
            <div><strong><CountUp end={15} suffix="+" /></strong><span>Years of industry experience</span></div>
            <div><strong><CountUp end={500} suffix="+" /></strong><span>Engineers mentored</span></div>
            <div><strong><CountUp end={8} suffix="+" /></strong><span>Product companies</span></div>
            <div><strong><CountUp end={lessonCount} /></strong><span>Free in-depth lessons</span></div>
          </div>
        </div>
        <ul className="mentor-points">
          {mentorPoints.map(({ icon: Icon, title, text }) => (
            <li key={title}>
              <span className="mentor-point-icon"><Icon /></span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const faqs = [
  { q: 'Are the Learn lessons really free?', a: `Yes. All ${lessonCount} lessons in the Learn section are free to read, with no sign-up. Live cohorts are the paid, mentor-led part of Codeazy.` },
  { q: 'I am a complete beginner. Where should I start?', a: 'Start with the free HTML, CSS and JavaScript lessons. If you want structure and mentorship, the Full-Stack Web Development course takes you from your first web page to a deployed application.' },
  { q: 'Which course should I choose?', a: `It depends on your goal: Full-Stack or Java Full-Stack to get your first developer job, DSA and System Design to crack interviews at product companies, and Agentic AI to build with LLMs. We currently offer ${courses.length} career-focused programs. Request a seat and a mentor will help you choose on a free call.` },
  { q: 'Are classes live or recorded?', a: 'Cohorts are taught live, so you can ask questions in real time. Every class is also recorded, so you can catch up if you miss one.' },
  { q: 'Do you help with interviews and jobs?', a: 'Yes. Courses include interview preparation such as mock interviews, resume and portfolio reviews, and guidance from mentors who have hired engineers themselves.' },
  { q: 'Do I need to pay to request a seat?', a: 'No. Requesting a seat is free. A mentor will call you to understand your goals and confirm the right batch before anything else.' }
];

export const Faq = () => (
  <section className="home-section">
    <div className="container faq-layout">
      <div className="home-section-head faq-head">
        <span className="section-eyebrow">FAQ</span>
        <h2>Questions, answered</h2>
        <p>Still unsure? <a href="#contact-form">Send us a message</a> and we will get back to you.</p>
      </div>
      <div className="faq-list">
        {faqs.map((f, i) => (
          <details className="faq-item" key={f.q} data-reveal style={{ '--i': i }}>
            <summary>
              {f.q}
              <AddIcon className="faq-icon" />
            </summary>
            <p>{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

export const FinalCta = () => (
  <section className="home-section">
    <div className="container">
      <div className="final-cta" data-reveal>
        <h2>Your next role starts with the next lesson.</h2>
        <p>Start learning for free today, or join a live cohort and get there faster with a mentor by your side.</p>
        <div className="home-hero-ctas">
          <Link to="/learn" className="btn-hero-primary">
            Start learning free <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Link>
          <Link to="/live-classes" className="btn-hero-secondary">Join a live cohort</Link>
        </div>
      </div>
    </div>
  </section>
);

export const ContactSection = () => (
  <section className="home-section contact-home" id="contact-form">
    <div className="container">
      <div className="home-section-head" data-reveal>
        <span className="section-eyebrow">Contact</span>
        <h2>Talk to us</h2>
        <p>Questions about courses, batches or fees? Leave your details and we will reach out.</p>
      </div>
      <ContactForm embedded={true} />
    </div>
  </section>
);
